<template>
  <main class="main main--has-floating-buttons">
    <portal v-if="!isEmpty(folios)" to="headerTitle">
      {{ $t(`folio.title.${folios.parent.type}`, [folios.parent.reservationNumber || folios.parent.guestProfileNumber]) }} / {{ $t('pageFolios.title.folios') }}
    </portal>
    <spinner-overlay v-if="creatingFolio" />
    <template v-if="!isEmpty(folios)">
      <card :title="$t('pageFolios.card.primaryGuest')" cols="12 6@tablet 4@desktop">
        <hm-col cols="12">
          <div class="privacy--blur"><strong>{{ folios.parent.name }}</strong></div>
          <div class="privacy--blur" v-html="folios.parent.address.address"></div>
          <div class="privacy--blur" v-html="`${folios.parent.address.zipCode == null ? '' : folios.parent.address.zipCode} ${folios.parent.address.city == null ? '' : folios.parent.address.city}`"></div>
          <div v-html="getI18nCountry(folios.parent.address.country)"></div>
        </hm-col>
      </card>
      <card v-if="folios.parent.type === 'RoomReservation'" :title="$t('pageFolios.card.reservationDetails')" cols="12 6@tablet 4@desktop">
        <hm-col class="text--bold" cols="6">{{ $t('pageFolios.arrivalDate') }}:</hm-col><hm-col cols="6">{{ folios.parent.checkInDate | toDate }}</hm-col>
        <hm-col class="text--bold" cols="6">{{ $t('pageFolios.departureDate') }}:</hm-col><hm-col cols="6">{{ folios.parent.checkOutDate | toDate }}</hm-col>
        <hm-col class="text--bold" cols="12">&nbsp;</hm-col>
        <hm-col class="text--bold" cols="6">{{ $t('pageFolios.reservationStatus') }}:</hm-col><hm-col cols="6">{{ $t(`ReservationStatus.${folios.parent.status}`) }}</hm-col>
        <hm-col class="text--bold" cols="6">{{ $t('pageFolios.roomType') }}:</hm-col><hm-col cols="6">{{ findHotelSettingById('roomTypes', folios.parent.roomTypeId).name[$shortLocale] }}</hm-col>
        <hm-col class="text--bold" cols="6">{{ $t('pageFolios.ratePlan') }}:</hm-col><hm-col cols="6">{{ findHotelSettingById('ratePlans', folios.parent.ratePlanId).name[$shortLocale] }}</hm-col>
        <hm-col class="text--bold" cols="6">{{ $t('pageFolios.roomNumber') }}:</hm-col><hm-col cols="6">{{ folios.parent.roomNumber }}</hm-col>
      </card>
      <card :title="$t('pageFolios.card.paymentStatus')" cols="12 6@tablet 4@desktop">
        <payment-status :payment-status="folios.paymentStatus" />
      </card>

      <card :title="$t('pageFolios.card.invoiceOverview')" cols="12">
        <hm-filter :rows="folios.folios" @filter="filteredFolios = $event">
          <hm-col cols="5">
            <p class="explanation-text">{{ $t('pageFolios.hintUseFilterFunction') }}</p>
          </hm-col>
        </hm-filter>
        <hm-card-inner :full-size="true">
          <folio-table :folios="filteredFolios" />
        </hm-card-inner>
      </card>
    </template>
    <hm-floating-buttons v-if="hasAnyRole(['ROLE_INVOICING', 'ROLE_ADMIN', 'ROLE_SYSADMIN'])" ref="floatingButtons">
      <hm-floating-button :label="$t('pageFolios.actions.more')" icon="plus" color="blue">
        <hm-floating-child-button :label="$t('pageFolios.actions.addFolio')"
                                  :disabled="folioCount >= 50"
                                  icon="plus"
                                  color="blue"
                                  @click="addFolio" />
      </hm-floating-button>
    </hm-floating-buttons>
  </main>
</template>


<script>
/* eslint func-names: 0 */

import HotelmeisterMixin from '@/mixins/HotelmeisterMixin';
import { isArray, join, isNil, isEmpty, groupBy, values, head, cloneDeep, concat, unionBy, reverse, sortBy } from 'lodash';
import HmCardInnerLine from '../lib/layout/HmCardInnerLine';
import HmCardInner from '../lib/layout/HmCardInner';
import uuid from 'uuid';
import HmCol from '../lib/layout/HmCol';
import HmInput from '../lib/inputs/HmInput';
import ContenteditableDiv from '../lib/util/ContenteditableDiv';
import NotificationMixin from '../../mixins/NotificationMixin';
import HmTable from '../lib/table/HmTable';
import TabableCard from '../lib/layout/TabableCard';
import TabableCardContent from '../lib/layout/TabableCardContent';
import PaymentStatus from '../lib/util/PaymentStatus';
import FilterMixin from '../../mixins/FilterMixin';
import HmFloatingButton from '../lib/inputs/HmFloatingButton.vue';
import HmFloatingChildButton from '../lib/inputs/HmFloatingChildButton.vue';

export default {
  name: 'PageFolios',
  components: { HmFloatingChildButton, HmFloatingButton, PaymentStatus, TabableCardContent, TabableCard, HmTable, ContenteditableDiv, HmCardInner, HmInput, HmCol },

  mixins: [HotelmeisterMixin, NotificationMixin, FilterMixin],

  data() {
    return {
      filteredFolios: [],
      folios: {},
      creatingFolio: false,
    };
  },

  computed: {
    parentId: function () {
      return this.$route.params.reservationId || this.$route.params.guestProfileId;
    },
    folioCount: function () {
      try {
        return this.folios.folios.length;
      } catch (e) {
        return 0;
      }
    },
  },

  watch: {
  },

  created: function () {
    this.$serviceBus.$on('folio.list', (data) => {
      // eslint-disable-next-line no-multi-assign
      this.folios = cloneDeep(data.data);

      this.folios.folios = sortBy(this.folios.folios, [it => (it.balance === 0 ? 1 : 0), it => it.number]);

      this.filteredFolios = this.folios.folios;

      if (!isEmpty(this.folios.parent.roomChargeInvoiceRecipientId)) {
        this.updateAdditionalFolios(this.folios.parent.roomChargeInvoiceRecipientId);
      }
    });

    this.updateFolios();
  },

  methods: {
    addFolio() {
      this.$store.commit('save/started', this.$route.params.folioId);
      this.creatingFolio = true;
      this.axios.post(this.serviceUrls.folio.createFolio(this, this.parentId))
        .then((response) => {
          this.updateFolios();
        })
        .catch((error) => {
          this.showMessages(error.response);
        })
        .finally(() => {
          this.creatingFolio = false;
          this.$store.commit('save/finished', this.$route.params.folioId);
        });
    },
    routeToFolio(folioId) {
      this.$router.push({
        name: `${this.$route.meta.parent}Folio`,
        params: this.routeParams(folioId),
      });
    },
    routeParams(folioId) {
      const ret = cloneDeep(this.$route.params);
      ret.folioId = folioId;
      return ret;
    },
    updateFolios() {
      this.axios.get(this.serviceUrls.folio.list(this, this.parentId))
        .then((response) => {
          this.$serviceBus.$emit('folio.list', {
            parentId: this.parentId,
            data: response.data.data,
          });
        });
    },
    updateAdditionalFolios(parentId) {
      this.axios.get(this.serviceUrls.folio.list(this, parentId))
        .then((response) => {
          this.$serviceBus.$emit('additionalFolio.list', {
            parentId: this.parentId,
            data: response.data.data,
          });
        });
    },
  },
};
</script>

<!-- Scoped SCSS  -->
<style lang="scss" scoped>
  @import '~styles/import';
</style>



// WEBPACK FOOTER //
// src/components/pages/PageFolios.vue